import { ArrowLeft, Flame, TrendingUp } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

const CaloriesBurnedDetail = () => {
  const navigate = useNavigate();

  const weeklyData = [
    { day: "Mon", calories: 420 },
    { day: "Tue", calories: 380 },
    { day: "Wed", calories: 450 },
    { day: "Thu", calories: 410 },
    { day: "Fri", calories: 480 },
    { day: "Sat", calories: 390 },
    { day: "Sun", calories: 420 },
  ];

  const monthlyData = [
    { week: "Week 1", calories: 2940 },
    { week: "Week 2", calories: 3120 },
    { week: "Week 3", calories: 2850 },
    { week: "Week 4", calories: 3200 },
  ];

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-border/50 p-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/fitness")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-foreground">
              Calories Burned
            </h1>
            <p className="text-xs text-muted-foreground">
              Track your energy expenditure
            </p>
          </div>
        </div>

        <div className="space-y-4 p-4">
          {/* Today's Summary */}
          <Card className="rounded-2xl border-0 bg-gradient-to-br from-accent/5 to-accent/10 p-5 shadow-sm">
            <div className="mb-4 flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-accent/20">
                <Flame className="h-7 w-7 text-accent" />
              </div>
              <div className="flex-1">
                <div className="text-3xl font-bold text-foreground">420</div>
                <p className="text-sm text-muted-foreground">
                  kcal burned today
                </p>
              </div>
            </div>
          </Card>

          {/* Info Card */}
          <Card className="rounded-2xl border-0 bg-muted/30 p-4 shadow-sm">
            <div className="flex gap-3">
              <TrendingUp className="mt-0.5 h-5 w-5 flex-shrink-0 text-accent" />
              <div>
                <h4 className="mb-1 text-sm font-semibold text-foreground">
                  About Calories Burned
                </h4>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Calories burned includes basal metabolic rate, activity
                  calories, and exercise. Fitbit estimates this using your heart
                  rate, movement patterns, and personal metrics like age,
                  weight, and height.
                </p>
              </div>
            </div>
          </Card>

          {/* Tabs for Time Periods */}
          <Tabs defaultValue="weekly" className="w-full">
            <TabsList className="mb-4 grid w-full grid-cols-2">
              <TabsTrigger value="weekly">Weekly</TabsTrigger>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
            </TabsList>

            <TabsContent value="weekly" className="space-y-4">
              <Card className="rounded-2xl border-0 p-5 shadow-sm">
                <h3 className="mb-4 font-semibold text-foreground">
                  This Week's Calories
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={weeklyData}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="hsl(var(--border))"
                        opacity={0.3}
                      />
                      <XAxis
                        dataKey="day"
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                      />
                      <YAxis
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                        }}
                      />
                      <Bar
                        dataKey="calories"
                        fill="hsl(var(--accent))"
                        radius={[8, 8, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="monthly" className="space-y-4">
              <Card className="rounded-2xl border-0 p-5 shadow-sm">
                <h3 className="mb-4 font-semibold text-foreground">
                  This Month's Calories
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlyData}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="hsl(var(--border))"
                        opacity={0.3}
                      />
                      <XAxis
                        dataKey="week"
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                      />
                      <YAxis
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                        }}
                      />
                      <Bar
                        dataKey="calories"
                        fill="hsl(var(--accent))"
                        radius={[8, 8, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default CaloriesBurnedDetail;
